import { useState } from 'react'
import { Check, Loader2, RotateCw } from 'lucide-react'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/shared/components/ui/command'
import { formatTimeShort } from '@/shared/lib/DateUtil'
import { cn } from '@/shared/lib/utils'
import { CHAT_COPY, type BroadcastShift } from '@/entities/chat'
import { defaultGuardIds } from './broadcastableShifts'

function shiftStatus(shift: BroadcastShift): { label: string; tone: string } {
  if (shift.overtime) return { label: CHAT_COPY.shiftOvertime, tone: 'bg-warn-bg text-warn' }
  if (new Date(shift.startTime).getTime() <= Date.now()) return { label: CHAT_COPY.shiftRunning, tone: 'bg-good-bg text-good' }
  return { label: CHAT_COPY.shiftStartsAt(formatTimeShort(shift.startTime)), tone: 'bg-surface-mute text-ink-3' }
}

export function ShiftAudiencePicker({
  shifts,
  isLoading,
  isError,
  onRetry,
  shiftId,
  onShiftChange,
  selectedGuardIds,
  onSelectedGuardIdsChange,
}: {
  shifts: BroadcastShift[]
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  shiftId: number | null
  onShiftChange: (shiftId: number, guardIds: number[]) => void
  selectedGuardIds: number[]
  onSelectedGuardIdsChange: (guardIds: number[]) => void
}) {
  const [search, setSearch] = useState('')
  const selectedShift = shifts.find((shift) => shift.shiftId === shiftId)

  const toggleGuard = (guardId: number, checked: boolean) =>
    onSelectedGuardIdsChange(
      checked ? [...selectedGuardIds, guardId] : selectedGuardIds.filter((id) => id !== guardId),
    )

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <p className="text-[12.5px] font-medium text-ink-2">Shift *</p>
        <Command className="rounded-md border border-line">
          <CommandInput value={search} onValueChange={setSearch} placeholder={CHAT_COPY.shiftSearch} />
          <CommandList className="max-h-[220px]">
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-ink-4" />
              </div>
            ) : isError ? (
              <button
                type="button"
                onClick={onRetry}
                className="flex w-full items-center justify-center gap-1.5 px-3 py-6 text-[12.5px] text-ink-3 hover:bg-hover"
              >
                <RotateCw className="h-3.5 w-3.5" />
                {CHAT_COPY.shiftsFailed}
              </button>
            ) : (
              <>
                <CommandEmpty>{shifts.length === 0 ? CHAT_COPY.noBroadcastShifts : CHAT_COPY.noShiftMatch}</CommandEmpty>
                {shifts.map((shift) => {
                  const status = shiftStatus(shift)
                  return (
                    <CommandItem
                      key={shift.shiftId}
                      value={`${shift.siteName} ${shift.title} ${shift.shiftId}`}
                      onSelect={() => onShiftChange(shift.shiftId, defaultGuardIds(shift))}
                    >
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate">
                          {shift.siteName}
                          {shift.title && shift.title !== shift.siteName && (
                            <span className="text-ink-4"> · {shift.title}</span>
                          )}
                        </span>
                        <span className="flex items-center gap-1.5 text-[11.5px] text-ink-4">
                          <span className={cn('rounded px-1.5 py-px text-[10.5px] font-medium', status.tone)}>
                            {status.label}
                          </span>
                          {formatTimeShort(shift.startTime)}–{formatTimeShort(shift.endTime)} ·{' '}
                          {CHAT_COPY.guardCount(shift.guards.length)}
                        </span>
                      </div>
                      {shift.shiftId === shiftId && <Check className="ml-auto h-3.5 w-3.5 shrink-0 text-brand-accent-ink" />}
                    </CommandItem>
                  )
                })}
              </>
            )}
          </CommandList>
        </Command>
      </div>

      {selectedShift && (
        <div className="space-y-1.5">
          <p className="text-[12.5px] font-medium text-ink-2">Guards on this shift</p>
          <div className="flex max-h-[180px] flex-col gap-1 overflow-y-auto rounded-md border border-line p-2">
            {selectedShift.guards.map((guard) => (
              <label key={guard.userId} className="flex cursor-pointer items-center gap-2 text-[13px] text-ink">
                <Checkbox
                  checked={selectedGuardIds.includes(guard.userId)}
                  onCheckedChange={(checked) => toggleGuard(guard.userId, checked === true)}
                />
                <span className="flex-1 truncate">{guard.name || CHAT_COPY.guardNameUnavailable}</span>
                {guard.onDuty && (
                  <span className="rounded bg-good-bg px-1.5 py-px text-[10.5px] font-medium text-good">
                    {CHAT_COPY.guardOnDuty}
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
